import {Injectable} from '@angular/core';
import {AuthStrategy} from '../../smoothr-web-app-core/models/AuthStrategy';
import {CompressedStorageService} from '../../smoothr-web-app-core/services/compression';
import {Api} from '../../smoothr-web-app-core/api/api';
import {RepositoryService} from '../../smoothr-web-app-core/services/repository/repository.service';
import {CustomerAuth} from '../../smoothr-web-app-core/models/CustomerAuth';

const ACCESS_TOKEN_TIMEOUT = 10000;

@Injectable({
	providedIn: 'root'
})
export class HelloAgainService {

	private pendingAutoLogin: Promise<boolean> = null;

	get isUserRedirectedFromHelloAgain() {
		return this.compressedStorageService.getItem(AuthStrategy.HELLOAGAIN);
	}

	constructor(
		private compressedStorageService: CompressedStorageService,
		private repository: RepositoryService
	) {}

	finishRedirectProcess() {
		this.compressedStorageService.removeItem(AuthStrategy.HELLOAGAIN);
	}

	async runUserAuthorization(trackUseRedirect = false) {
		// Inside the native app the access token comes from the webview, no redirect needed
		if (window.ReactNativeWebView && await this.autoLoginWithWebViewToken()) {
			return;
		}

		const res = await Api.loginHelloAgain(AuthStrategy.HELLOAGAIN).catch(err => console.error(err));
		if (trackUseRedirect) this.compressedStorageService.setItem(AuthStrategy.HELLOAGAIN, true);
		if (res) window.location.href = res.data.url;
	}

	autoLoginWithWebViewToken(): Promise<boolean> {
		if (!window.ReactNativeWebView) {
			return Promise.resolve(false);
		}
		// The webview bridge answers a single request at a time, so later callers join the running one
		if (!this.pendingAutoLogin) {
			this.pendingAutoLogin = this.requestAccessToken()
				.then(token => (token ? this.loginWithAccessToken(token) : false))
				.catch(err => {
					console.error('Hello Again auto-login failed', err);
					return false;
				})
				.finally(() => (this.pendingAutoLogin = null));
		}
		return this.pendingAutoLogin;
	}

	private requestAccessToken(): Promise<string> {
		return new Promise<string>(resolve => {
			let settled = false;
			let timeout: any;

			const finish = (token: string) => {
				if (settled) {
					return;
				}
				settled = true;
				clearTimeout(timeout);
				document.removeEventListener('message', onMessage);
				window.removeEventListener('message', onMessage);
				resolve(token);
			};

			// The webview delivers the same payload on both document and window
			const onMessage = (event: any) => {
				const token = this.readAccessToken(event?.data);
				if (token) {
					finish(token);
				}
			};

			document.addEventListener('message', onMessage);
			window.addEventListener('message', onMessage);
			timeout = setTimeout(() => finish(null), ACCESS_TOKEN_TIMEOUT);

			try {
				window.ReactNativeWebView.postMessage(JSON.stringify({
					action: 'getAccessToken',
				}));
			} catch (err) {
				console.error('Hello Again auto-login: native bridge unreachable', err);
				finish(null);
			}
		});
	}

	// The native app posts a JSON string, older builds post the object itself
	private readAccessToken(data: any): string {
		if (!data) {
			return null;
		}
		let payload = data;
		if (typeof payload === 'string') {
			try {
				payload = JSON.parse(payload);
			} catch (err) {
				return null;
			}
		}
		return payload?.token ?? payload?.accessToken ?? null;
	}

	private async loginWithAccessToken(accessToken: string): Promise<boolean> {
		const res = (await Api.helloAgainAutoLogin(AuthStrategy.HELLOAGAIN, accessToken)).data;
		const customerAuth = await this.toCustomerAuth(res);
		if (!customerAuth?.token) {
			console.error('Hello Again auto-login: unexpected response', res);
			return false;
		}
		// Hands the session to the repository, which stores it and sets the api tokens
		this.repository.customerAuth.emit(customerAuth);
		return true;
	}

	// The endpoint either returns the session itself or the one time login of the redirect flow
	private async toCustomerAuth(res: any): Promise<CustomerAuth> {
		if (res?.token) {
			return res;
		}
		if (res?.id && res?.auth) {
			return (await Api.oneTimeLogin(res.id, res.auth)).data;
		}
		return null;
	}
}
